import { NavLink, useNavigate } from "react-router-dom";
import { Bot, BarChart3, KeyRound, FileSignature, Settings, LockKeyhole, ChevronLeft, TerminalSquare, Activity, Boxes } from "lucide-react";
import { useSchema } from "@/context/SchemaContext";
import { useAuth } from "@/context/AuthContext";
import { Icon } from "@/lib/icons";

const ADMIN = [
  { to: "/admin/reports", label: "التقارير والإحصائيات", icon: BarChart3, tid: "sidebar-admin-reports-button" },
  { to: "/admin/vault", label: "خزنة المفاتيح والرموز", icon: KeyRound, tid: "sidebar-admin-vault-button" },
  { to: "/admin/contracts", label: "العقود والبيع", icon: FileSignature, tid: "sidebar-admin-contracts-button" },
  { to: "/admin/settings", label: "الإعدادات والجلسات", icon: Settings, tid: "sidebar-admin-settings-button" },
];

export default function Sidebar({ onNavigate }) {
  const { factories } = useSchema();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const list = Object.values(factories).filter((f) => f.id !== "main");

  return (
    <aside className="h-full w-72 flex flex-col bg-[#090D16] border-l border-white/[0.06]" data-testid="sidebar">
      <div className="px-5 pt-6 pb-4 border-b border-white/[0.06]">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-cyan-400/30 to-purple-500/30 border border-white/10 grid place-items-center text-cyan-200">
            <Icon name="Factory" size={20} />
          </div>
          <div>
            <p className="font-bold text-slate-50 leading-tight">مصنع المصانع الأم</p>
            <p className="text-[11px] font-mono text-cyan-300/80 tracking-widest">AQL4.COM</p>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-2 text-[11px] text-emerald-300">
          <span className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_10px_rgba(16,185,129,0.9)]" /> النظام يعمل · مقفل للمالك
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-6">
        <div className="space-y-1">
          <NavLink to="/" end onClick={onNavigate} data-testid="sidebar-home-button" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
            <Bot size={18} className="text-slate-100" /> <span className="flex-1">الوكيل الذكي الرئيسي</span>
          </NavLink>
          <NavLink to="/shell" onClick={onNavigate} data-testid="sidebar-shell-button" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
            <TerminalSquare size={18} className="text-lime-300" /> <span className="flex-1">الشِل السيادي — الأوامر والتكامل</span>
          </NavLink>
          <NavLink to="/work" onClick={onNavigate} data-testid="sidebar-work-button" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
            <Activity size={18} className="text-teal-300" /> <span className="flex-1">مركز العمل والمراقبة</span>
          </NavLink>
          <NavLink to="/factories" onClick={onNavigate} data-testid="sidebar-factories-button" className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
            <Boxes size={18} className="text-violet-300" /> <span className="flex-1">قسم المصانع</span>
          </NavLink>
        </div>
        <div>
          <p className="px-3 mb-2 text-[11px] tracking-wide text-slate-500">المصانع</p>
          <div className="space-y-1">
            {list.map((f, i) => (
              <NavLink key={f.id} to={`/factory/${f.id}`} onClick={onNavigate} data-testid={`sidebar-factory-${i + 1}-button`}
                className={({ isActive }) => `nav-item group ${isActive ? "active" : ""}`}
                style={({ isActive }) => (isActive ? { boxShadow: `inset 3px 0 0 ${f.color}` } : {})}>
                <span className="h-8 w-8 rounded-lg grid place-items-center border" style={{ color: f.color, background: `${f.color}14`, borderColor: `${f.color}33` }}>
                  <Icon name={f.icon} size={16} />
                </span>
                <span className="flex-1 leading-tight">
                  <span className="block text-[10px] text-slate-500">مصنع {i + 1}</span>
                  {f.name}
                </span>
                <ChevronLeft size={14} className="text-slate-600 group-hover:text-slate-300 transition-colors" />
              </NavLink>
            ))}
          </div>
        </div>
        <div>
          <p className="px-3 mb-2 text-[11px] tracking-wide text-slate-500">الإدارة العامة</p>
          <div className="space-y-1">
            {ADMIN.map((a) => (
              <NavLink key={a.to} to={a.to} onClick={onNavigate} data-testid={a.tid} className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
                <a.icon size={17} className="text-slate-400" /> {a.label}
              </NavLink>
            ))}
          </div>
        </div>
      </nav>

      <div className="p-3 border-t border-white/[0.06]">
        <button data-testid="lock-session-button" onClick={async () => { await logout(); navigate("/"); }}
          className="w-full flex items-center justify-center gap-2 rounded-xl bg-white/[0.04] hover:bg-red-500/10 hover:text-red-300 text-slate-300 py-2.5 text-sm transition-colors">
          <LockKeyhole size={16} /> قفل الجلسة
        </button>
      </div>
    </aside>
  );
}
